import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { PATHS, type Path } from "@/content/en/paths";
import { ideaBySlug } from "@/content/en/ideas";
import { conceptBySlug } from "@/content/en/concepts";
import { frameworkBySlug } from "@/content/en/frameworks";
import { researchBySlug } from "@/content/en/research";

function countTotal(p: Path): number {
  return (
    p.contents.essays.length +
    p.contents.concepts.length +
    p.contents.frameworks.length +
    p.contents.sourceNotes.length
  );
}

export function ExploreMap() {
  const [openId, setOpenId] = useState<string | null>(PATHS[1]?.id ?? null);

  return (
    <section
      id="explore"
      aria-labelledby="explore-heading"
      className="py-14 md:py-20 border-t border-ink/10"
    >
      <div className="mb-10 md:mb-12 flex items-end justify-between gap-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-clay font-semibold">
            Explore the work
          </p>
          <h2
            id="explore-heading"
            className="mt-3 font-serif text-3xl md:text-4xl tracking-tight text-balance"
          >
            Enter through an idea, not a category.
          </h2>
          <p className="mt-3 max-w-2xl text-[15px] text-ink/70 leading-relaxed">
            Five paths through the writing. Each opens onto the essays,
            concepts, frameworks, and source notes that live under the same
            question.
          </p>
        </div>
      </div>

      {/* Map: 5 paths, one open at a time on desktop; stacked on mobile */}
      <ol className="divide-y divide-ink/15 border-y border-ink/15">
        {PATHS.map((p) => {
          const open = openId === p.id;
          const total = countTotal(p);
          return (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => setOpenId(open ? null : p.id)}
                aria-expanded={open}
                aria-controls={`path-${p.id}`}
                className="w-full text-left py-6 md:py-7 grid grid-cols-[2.5rem_1fr_auto] items-baseline gap-4 md:gap-6 group"
              >
                <span className="font-serif text-sm text-ink/50">
                  {p.numeral}
                </span>
                <div>
                  <h3
                    className={`font-serif text-2xl md:text-3xl leading-snug text-balance transition-colors ${
                      open ? "text-clay" : "text-ink group-hover:text-clay"
                    }`}
                  >
                    {p.title}
                  </h3>
                  {!open && (
                    <p className="mt-2 text-[14px] md:text-[15px] text-ink/70 leading-relaxed max-w-2xl">
                      {p.lede}
                    </p>
                  )}
                </div>
                <span className="text-[10px] uppercase tracking-[0.22em] text-ink/50 whitespace-nowrap">
                  {total} entries
                  <span
                    aria-hidden
                    className={`ml-3 inline-block transition-transform ${
                      open ? "rotate-45 text-clay" : ""
                    }`}
                  >
                    +
                  </span>
                </span>
              </button>

              {open && (
                <div
                  id={`path-${p.id}`}
                  className="pb-10 md:pb-12 pl-0 md:pl-[3.5rem]"
                >
                  <p className="mb-8 max-w-2xl font-serif italic text-lg text-ink/80 leading-snug">
                    {p.lede}
                  </p>
                  <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                    <PathColumn
                      label="Essays"
                      empty="No essays under this path yet."
                      items={p.contents.essays
                        .map((s) => ideaBySlug(s))
                        .filter((x): x is NonNullable<typeof x> => Boolean(x))
                        .map((x) => ({
                          key: x.slug,
                          title: x.title,
                          subtitle: x.subtitle,
                          node: (
                            <Link
                              to="/ideas/$slug"
                              params={{ slug: x.slug }}
                              className="group block"
                            >
                              <p className="font-serif text-lg text-ink group-hover:text-clay leading-snug text-balance">
                                {x.title}
                              </p>
                              <p className="mt-1 text-[13px] text-ink/65 leading-relaxed">
                                {x.subtitle}
                              </p>
                            </Link>
                          ),
                        }))}
                    />
                    <PathColumn
                      label="Concepts"
                      empty="No concepts under this path yet."
                      items={p.contents.concepts
                        .map((s) => conceptBySlug(s))
                        .filter((x): x is NonNullable<typeof x> => Boolean(x))
                        .map((x) => ({
                          key: x.slug,
                          title: x.name,
                          subtitle: x.oneLine,
                          node: (
                            <Link
                              to="/concepts/$slug"
                              params={{ slug: x.slug }}
                              className="group block"
                            >
                              <p className="font-serif italic text-base text-ink group-hover:text-clay leading-snug">
                                {x.name}
                              </p>
                              <p className="mt-1 text-[13px] text-ink/65 leading-relaxed line-clamp-2">
                                {x.oneLine}
                              </p>
                            </Link>
                          ),
                        }))}
                    />
                    <PathColumn
                      label="Frameworks"
                      empty="No frameworks under this path yet."
                      items={p.contents.frameworks
                        .map((s) => frameworkBySlug(s))
                        .filter((x): x is NonNullable<typeof x> => Boolean(x))
                        .map((x) => ({
                          key: x.slug,
                          title: x.name,
                          subtitle: x.coreQuestion,
                          node: (
                            <Link
                              to="/frameworks/$slug"
                              params={{ slug: x.slug }}
                              className="group block"
                            >
                              <p className="font-serif text-base text-ink group-hover:text-clay leading-snug">
                                {x.name}
                              </p>
                              <p className="mt-1 text-[13px] text-ink/65 leading-relaxed line-clamp-2">
                                {x.coreQuestion}
                              </p>
                            </Link>
                          ),
                        }))}
                    />
                    <PathColumn
                      label="Source notes"
                      empty="No source notes under this path yet."
                      items={p.contents.sourceNotes
                        .map((s) => researchBySlug(s))
                        .filter((x): x is NonNullable<typeof x> => Boolean(x))
                        .map((x) => ({
                          key: x.slug,
                          title: x.title,
                          subtitle: `${x.institution} · ${x.year}`,
                          node: (
                            <Link
                              to="/research/$slug"
                              params={{ slug: x.slug }}
                              className="group block"
                            >
                              <p className="font-serif text-base text-ink group-hover:text-clay leading-snug">
                                {x.title}
                              </p>
                              <p className="mt-1 text-[11px] uppercase tracking-[0.14em] text-ink/55">
                                {x.institution} · {x.year}
                              </p>
                            </Link>
                          ),
                        }))}
                    />
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function PathColumn({
  label,
  items,
  empty,
}: {
  label: string;
  items: Array<{ key: string; title: string; subtitle: string; node: React.ReactNode }>;
  empty: string;
}) {
  return (
    <div>
      <p className="mb-4 text-[10px] uppercase tracking-[0.24em] text-clay font-semibold">
        {label}
        <span className="ml-2 text-ink/40 font-normal">({items.length})</span>
      </p>
      {items.length === 0 ? (
        <p className="text-[13px] italic text-ink/50">{empty}</p>
      ) : (
        <ul className="space-y-4">
          {items.map((it) => (
            <li key={it.key}>{it.node}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
